import {
  useIsAssetLifecycleManagementEnabled,
  useProjectAssetLifecyclePolicy,
} from '@frameio/content-security/components/AssetLifecyclePolicy';
import { plural } from '@frameio/i18n';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { MediaThumbnailLabel, theme, Tooltip } from '@frameio/vapor';
import { IconClock } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/AssetLifecycleExpirationLabel';

type Props = {
  archiveFrom: string | null | undefined;
};

export const AssetLifecycleExpirationLabel = ({ archiveFrom }: Props) => {
  const projectId = useActiveProjectId();
  const projectPolicy = useProjectAssetLifecyclePolicy(projectId);
  const isALMEnabled = useIsAssetLifecycleManagementEnabled();
  const policyDaysBeforeDeletion =
    projectPolicy.workspace.daysBeforeAssetAutoDeletion;

  // if asset lifecycle management is not enabled on the current plan, or
  // if for some reason the archiveFrom is null, we can't display the label
  if (!isALMEnabled || typeof archiveFrom !== 'string') {
    return null;
  }

  // if either the project is exempt or the workspace policy is disabled we
  // do not display any label
  if (
    projectPolicy.isExempt ||
    typeof policyDaysBeforeDeletion !== 'number' ||
    policyDaysBeforeDeletion === 0
  ) {
    return null;
  }

  // calculate the number of days until expiration
  const now = new Date().getTime();
  const archiveFromDate = new Date(archiveFrom).getTime();
  const daysBeforeDeletion =
    archiveFromDate + policyDaysBeforeDeletion * ONE_DAY_IN_MS;
  const timeUntilExpiration = daysBeforeDeletion - now;

  // never display a value below 1d as the worker will most likely delete
  // the asset during the next cycle
  let daysUntilExpiration = Math.round(timeUntilExpiration / ONE_DAY_IN_MS);
  if (daysUntilExpiration < 1) {
    daysUntilExpiration = 1;
  }

  return (
    <Tooltip
      placement="top"
      title={TEXT.expirationTooltip(daysUntilExpiration)}
    >
      <MediaThumbnailLabel
        aria-label={String(TEXT.expirationTooltip(daysUntilExpiration))}
        role="img"
        style={{
          padding: theme.spaces[2],
          display: 'flex',
          gap: theme.spaces[2],
        }}
      >
        <IconClock
          aria-hidden={true}
          color={theme.colors.icon.interactive.notice}
          size={ICON_SIZE}
        />
        {TEXT.daysUntilExpiration(daysUntilExpiration)}
      </MediaThumbnailLabel>
    </Tooltip>
  );
};

const ICON_SIZE = 16;
const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;

TEXT.DEFINE({
  expirationTooltip: (days: number) =>
    plural(days, {
      one: `This asset will delete in ${days} day`,
      other: `This asset will delete in ${days} days`,
    }),
  daysUntilExpiration: (days: number) => `${days}d`,
});
